import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { FaStore } from "react-icons/fa";
import {
  FiUser,
  FiMapPin,
  FiCalendar,
  FiBookOpen,
  FiMail,
  FiPhone,
  FiBriefcase,
  FiArrowLeft,
  FiSave
} from "react-icons/fi";
import Swal from "sweetalert2";

const EditKaryawan = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    fullname: "",
    tempat_lahir: "",
    tanggal_lahir: "",
    alamat: "",
    gender: "Laki-laki",
    pendidikan: "",
    no_hp: "",
    email: "",
    join_date: "",
    penempatan: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchKaryawan = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/v1/employee`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const rawList = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.data?.data)
          ? res.data.data.data
          : [];

        const employee = rawList.find((emp) => String(emp.id) === String(id));

        if (employee) {
          setFormData({
            fullname: employee.full_name || employee.fullname || "",
            tempat_lahir: employee.tempat_lahir || "",
            tanggal_lahir: employee.tanggal_lahir ? employee.tanggal_lahir.substring(0, 10) : "",
            alamat: employee.alamat || "",
            gender: employee.gender || "Laki-laki",
            pendidikan: employee.pendidikan || "",
            no_hp: employee.no_hp || "",
            email: employee.email || "",
            join_date: employee.join_date ? employee.join_date.substring(0, 10) : "",
            penempatan: employee.outlet?.id ? String(employee.outlet.id) : employee.penempatan ? String(employee.penempatan) : "",
          });
        } else {
          setError("Karyawan tidak ditemukan.");
        }
      } catch (err) {
        console.error("Error fetching employee detail:", err);
        setError("Gagal memuat data karyawan.");
      } finally {
        setLoading(false);
      }
    };

    const fetchOutlets = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/v1/outlet`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const rawOutlets = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.data?.data)
          ? res.data.data.data
          : [];

        setOutlets(rawOutlets);
      } catch (err) {
        console.error("Error fetching outlets:", err);
      }
    };

    fetchKaryawan();
    fetchOutlets();
  }, [id, token]);

  const validate = () => {
    let newErrors = {};

    Object.entries(formData).forEach(([key, value]) => {
      if (!value && key !== "penempatan") newErrors[key] = "Wajib diisi!";
    });

    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email))
      newErrors.email = "Format email tidak valid!";

    if (formData.no_hp && !/^[0-9+]{8,15}$/.test(formData.no_hp))
      newErrors.no_hp = "Nomor HP tidak valid!";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      Swal.fire({
        title: "Form Belum Lengkap",
        text: "Mohon periksa kolom bertanda merah.",
        icon: "warning",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    setSaving(true);

    const payload = {
      fullname: formData.fullname,
      tempat_lahir: formData.tempat_lahir,
      tanggal_lahir: formData.tanggal_lahir,
      alamat: formData.alamat,
      gender: formData.gender,
      pendidikan: formData.pendidikan,
      no_hp: formData.no_hp,
      email: formData.email,
      join_date: formData.join_date,
      penempatan: formData.penempatan ? parseInt(formData.penempatan, 10) : null,
    };

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/v1/employee/update/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        await Swal.fire({
          title: "Diperbarui!",
          text: `Data karyawan "${formData.fullname}" berhasil diubah.`,
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
          customClass: { popup: "rounded-3xl" },
        });

        navigate("/enterprise/karyawan");
      }
    } catch (err) {
      console.error("Update error:", err);
      Swal.fire({
        title: "Gagal Mengubah",
        text: err.response?.data?.message || "Terjadi kesalahan server.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20 min-h-screen">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto my-20 p-6 bg-white rounded-3xl shadow-xl text-center space-y-4">
        <h2 className="text-lg font-bold text-rose-600">Kesalahan Memuat Data</h2>
        <p className="text-xs text-gray-500">{error}</p>
        <button
          onClick={() => navigate("/enterprise/karyawan")}
          className="px-4 py-2 rounded-xl bg-blue-600 text-white text-xs font-bold"
        >
          Kembali ke Daftar Karyawan
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      {/* Header Form */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/enterprise/karyawan")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Edit Karyawan: {formData.fullname}
            </h1>
            <p className="text-xs text-gray-500">
              Perbarui identitas, kontak, dan penempatan outlet karyawan ini
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Card Form */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white rounded-3xl p-6 md:p-8 shadow-xl shadow-gray-200/50 border border-gray-100"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nama Lengkap */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiUser className="text-blue-600 w-4 h-4" />
                Nama Lengkap
              </label>
              <input
                type="text"
                name="fullname"
                value={formData.fullname}
                onChange={handleChange}
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-semibold ${
                  errors.fullname ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
              {errors.fullname && <p className="text-rose-500 text-[11px] font-semibold">{errors.fullname}</p>}
            </div>

            {/* Tempat Lahir */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiMapPin className="text-blue-600 w-4 h-4" />
                Tempat Lahir
              </label>
              <input
                type="text"
                name="tempat_lahir"
                value={formData.tempat_lahir}
                onChange={handleChange}
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-semibold ${
                  errors.tempat_lahir ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
            </div>

            {/* Tanggal Lahir */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiCalendar className="text-blue-600 w-4 h-4" />
                Tanggal Lahir
              </label>
              <input
                type="date"
                name="tanggal_lahir"
                value={formData.tanggal_lahir}
                onChange={handleChange}
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${
                  errors.tanggal_lahir ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
            </div>

            {/* Gender */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiUser className="text-blue-600 w-4 h-4" />
                Jenis Kelamin (Gender)
              </label>
              <div className="flex gap-3 pt-1">
                {["Laki-laki", "Perempuan"].map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setFormData({ ...formData, gender: g })}
                    className={`flex-1 py-2.5 rounded-2xl text-xs font-bold transition-all ${
                      formData.gender === g
                        ? "bg-blue-600 text-white shadow-md shadow-blue-600/20"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            {/* Pendidikan */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiBookOpen className="text-blue-600 w-4 h-4" />
                Pendidikan Terakhir
              </label>
              <input
                type="text"
                name="pendidikan"
                value={formData.pendidikan}
                onChange={handleChange}
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-semibold ${
                  errors.pendidikan ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiMail className="text-blue-600 w-4 h-4" />
                Alamat Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-semibold ${
                  errors.email ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
              {errors.email && <p className="text-rose-500 text-[11px] font-semibold">{errors.email}</p>}
            </div>

            {/* No. HP */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiPhone className="text-blue-600 w-4 h-4" />
                Nomor Handphone
              </label>
              <input
                type="tel"
                name="no_hp"
                value={formData.no_hp}
                onChange={handleChange}
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-semibold ${
                  errors.no_hp ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
              {errors.no_hp && <p className="text-rose-500 text-[11px] font-semibold">{errors.no_hp}</p>}
            </div>

            {/* Tanggal Bergabung */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiBriefcase className="text-blue-600 w-4 h-4" />
                Tanggal Bergabung (Join Date)
              </label>
              <input
                type="date"
                name="join_date"
                value={formData.join_date}
                onChange={handleChange}
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${
                  errors.join_date ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
            </div>

            {/* Penempatan Outlet */}
            <div className="space-y-2 md:col-span-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FaStore className="text-blue-600 w-4 h-4" />
                Penempatan Outlet
              </label>
              <select
                name="penempatan"
                value={formData.penempatan}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              >
                <option value="">-- Pilih Penempatan Outlet --</option>
                {outlets.map((outlet) => (
                  <option key={outlet.id} value={outlet.id}>
                    {outlet.name} ({outlet.kota || "Pusat"})
                  </option>
                ))}
              </select>
            </div>

            {/* Alamat Lengkap */}
            <div className="space-y-2 md:col-span-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiMapPin className="text-blue-600 w-4 h-4" />
                Alamat Tempat Tinggal
              </label>
              <textarea
                name="alamat"
                rows={3}
                value={formData.alamat}
                onChange={handleChange}
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-semibold ${
                  errors.alamat ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate("/enterprise/karyawan")}
              className="px-5 py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
            >
              Batal
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2"
            >
              <FiSave className="w-4 h-4" />
              <span>{saving ? "Menyimpan..." : "Simpan Perubahan"}</span>
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditKaryawan;
